import React, { ReactNode } from 'react';

/**
 * Only http(s) URLs survive this regex. Any other scheme (javascript:,
 * data:, mailto:, file:, …) does not match and the surrounding `[text](...)`
 * is left as literal text, so an upstream regression cannot slip a
 * dangerous URI past the renderer.
 *
 * The URL body stops at the first whitespace or `)` — the parser upstream
 * only emits bare Markdown links (no space, no closing paren inside the
 * URL), so this covers everything it produces and reads nothing extra
 * out of surrounding text.
 */
const MARKDOWN_LINK_PATTERN = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

/**
 * Walk a glossary definition string and emit React children for the
 * Popover body: plain text spans interleaved with real anchor elements
 * for any inline `[text](https?://url)` fragments. The glossary refresher
 * Lambda produces this shape for XWiki `[[visible>>url:http(s)://...]]`
 * links so the popover can surface them as clickable; non-URL link
 * targets upstream already collapse to visible text and so stay as
 * plain spans here.
 *
 * Anchors open in a new tab with `rel="noopener noreferrer"` so a
 * compromised target page cannot reach back into this one via
 * `window.opener`.
 *
 * Pure, so the Popover body can call it inline each render — the input
 * is short (<= one glossary definition) and the regex scan is O(n).
 */
export function renderDefinition(definition: string): ReactNode[] {
    const parts: ReactNode[] = [];
    let cursor = 0;
    let key = 0;
    MARKDOWN_LINK_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = MARKDOWN_LINK_PATTERN.exec(definition)) !== null) {
        if (match.index > cursor) {
            parts.push(definition.slice(cursor, match.index));
        }
        const [, text, url] = match;
        parts.push(
            <a
                key={`glossary-link-${key++}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
            >
                {text}
            </a>
        );
        cursor = match.index + match[0].length;
    }
    if (cursor < definition.length) {
        parts.push(definition.slice(cursor));
    }
    return parts;
}
